import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// Builds an array of the last N calendar days, each with a purchase count
function buildDayData(rawLog) {
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const entry = rawLog.find(e => e.date === dateStr);
    days.push({
      label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count: entry ? entry.count : 0,
    });
  }
  return days;
}

// Aggregates daily log into the last 12 Monday-anchored weeks
function buildWeekData(rawLog) {
  const weeks = [];
  // Find the most recent Monday
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sun
  const lastMonday = new Date(now);
  lastMonday.setDate(now.getDate() - ((dayOfWeek + 6) % 7));
  lastMonday.setHours(0, 0, 0, 0);

  for (let i = 11; i >= 0; i--) {
    const weekStart = new Date(lastMonday);
    weekStart.setDate(lastMonday.getDate() - i * 7);
    let count = 0;
    for (let j = 0; j < 7; j++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + j);
      const dateStr = d.toISOString().split('T')[0];
      const entry = rawLog.find(e => e.date === dateStr);
      if (entry) count += entry.count;
    }
    weeks.push({
      label: weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count,
    });
  }
  return weeks;
}

export default function PurchaseGraph({ articleId, articleTitle, onClose }) {
  const [view, setView] = useState('day');
  const [rawLog, setRawLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/articles/${articleId}/purchase-history`)
      .then(res => setRawLog(res.data.purchaseDateLog || []))
      .catch(() => setRawLog([]))
      .finally(() => setLoading(false));
  }, [articleId]);

  const chartData = view === 'day' ? buildDayData(rawLog) : buildWeekData(rawLog);
  const totalShown = chartData.reduce((s, d) => s + d.count, 0);

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', padding: '2rem',
        width: '700px', maxWidth: '95vw',
        boxShadow: '0 4px 32px rgba(0,0,0,0.22)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h4 style={{ margin: 0, fontSize: '1.1rem' }}>Purchase History</h4>
            <p style={{ color: '#888', fontSize: '0.82rem', margin: '0.2rem 0 0' }}>{articleTitle}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#888', lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>

        {/* Toggle */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {['day', 'week'].map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: '0.3rem 0.9rem', borderRadius: '5px', fontSize: '0.85rem', cursor: 'pointer',
                border: '1px solid #ccc',
                backgroundColor: view === v ? '#343a40' : '#fff',
                color: view === v ? '#fff' : '#333',
                fontWeight: view === v ? '600' : '400',
              }}
            >
              {v === 'day' ? 'Daily (30 days)' : 'Weekly (12 weeks)'}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: '0.82rem', color: '#888', alignSelf: 'center' }}>
            {totalShown} purchase{totalShown !== 1 ? 's' : ''} shown
          </span>
        </div>

        {/* Chart */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading...</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#888' }}
                interval={view === 'day' ? 4 : 0}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 10, fill: '#888' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(val) => [val, 'Purchases']}
                contentStyle={{ fontSize: '0.82rem', borderRadius: '6px', border: '1px solid #e0e0e0' }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#007bff"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: '#007bff' }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
